import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "src/components/pageHeader";
import { displayDate } from "src/utils/dateHandler.util";
import { timeAgo } from "src/utils/date.util";
import { FaArrowLeft, FaClock, FaHashtag, FaCreditCard, FaInfoCircle, FaStar } from "react-icons/fa";
import { PaymentTransaction } from "../types/payment-transaction.entity";

type PaymentTransactionDetailsProps = {
  transaction: (PaymentTransaction & { id: string });
  onBack?: () => void;
};

export function PaymentTransactionDetails({ transaction, onBack }: PaymentTransactionDetailsProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Safety check for transaction
  if (!transaction) {
    return (
      <div className="page_container">
        <PageHeader text="Payment Transaction Details" />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Transaction Not Found</h2>
          <p className="text-red-600 mb-6">The payment transaction could not be loaded.</p>
          {onBack && (
            <button
              onClick={onBack}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Additional safety check for malformed transaction
  if (typeof transaction !== 'object' || Array.isArray(transaction)) {
    return (
      <div className="page_container">
        <PageHeader text="Payment Transaction Details" />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Invalid Transaction Data</h2>
          <p className="text-red-600 mb-6">The transaction data is not in the expected format.</p>
          {onBack && (
            <button
              onClick={onBack}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('PaymentTransactionDetails transaction:', transaction);
  console.log('PaymentTransactionDetails transaction type:', typeof transaction);
  if (transaction) {
    console.log('PaymentTransactionDetails paymentService:', transaction.PaymentService);
    console.log('PaymentTransactionDetails paymentStatus:', transaction.paymentStatus);
    console.log('PaymentTransactionDetails meta:', transaction.meta);
  }

  const createdAt = (transaction as any).createdAt?.toDate?.() as Date | undefined;
  const updatedAt = (transaction as any).updatedAt?.toDate?.() as Date | undefined;

  const baseItemClasses = "transform transition-all duration-300 ease-out";
  const hiddenItemClasses = "opacity-0 translate-y-1";
  const visibleItemClasses = "opacity-100 translate-y-0";
  const cx = (extra: string = "") =>
    `${baseItemClasses} ${isVisible ? visibleItemClasses : hiddenItemClasses} ${extra}`.trim();
  const delayStyle = (ms: number) => ({ transitionDelay: `${ms}ms` } as React.CSSProperties);

  const statusBadgeClass = () => {
    if (!transaction.paymentStatus) return "bg-gray-100 text-gray-700 border-gray-200";
    
    switch (transaction.paymentStatus) {
      case "INITIALIZED":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "SUCCEEDED":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "CANCELED":
        return "bg-gray-100 text-gray-700 border-gray-200";
      case "FAILED":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const serviceBadgeClass = () => {
    if (!transaction.PaymentService) return "bg-gray-100 text-gray-700 border-gray-200";
    
    switch (transaction.PaymentService) {
      case "STRIPE":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "APPLE_PAY":
        return "bg-black text-white border-black";
      case "GOOGLE_PAY":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "WIX_PAYMENT":
        return "bg-green-100 text-green-700 border-green-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const renderMetaInfo = () => {
    try {
      if (!transaction.meta || !transaction.PaymentService) {
        return (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-200 rounded-lg">
                <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">?</span>
                </div>
              </div>
              <h4 className="font-bold text-gray-800 text-lg">No Payment Service Details</h4>
            </div>
            <div className="text-gray-600">
              <p>Payment service information is not available for this transaction.</p>
            </div>
          </div>
        );
      }
      
      const meta = transaction.meta;
      switch (transaction.PaymentService) {
      case "STRIPE":
        if ('stripePaymentIntentId' in meta) {
          return (
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-200 rounded-lg">
                  <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">S</span>
                  </div>
                </div>
                <h4 className="font-bold text-purple-800 text-lg">Stripe Payment Details</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-purple-200">
                  <span className="text-purple-700 font-medium">Payment Intent ID</span>
                  <span className="font-mono text-sm bg-purple-200 px-3 py-1 rounded-lg text-purple-800">{meta.stripePaymentIntentId}</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      case "APPLE_PAY":
        if ('appleTransactionId' in meta && 'originalTransactionId' in meta && 'productId' in meta) {
          return (
            <div className="bg-gradient-to-r from-gray-900 to-black text-white border border-gray-700 rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white rounded-lg">
                  <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">A</span>
                  </div>
                </div>
                <h4 className="font-bold text-white text-lg">Apple Pay Details</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-600">
                  <span className="text-gray-300 font-medium">Transaction ID</span>
                  <span className="font-mono text-sm bg-gray-800 px-3 py-1 rounded-lg text-white">{meta.appleTransactionId}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-600">
                  <span className="text-gray-300 font-medium">Original Transaction ID</span>
                  <span className="font-mono text-sm bg-gray-800 px-3 py-1 rounded-lg text-white">{meta.originalTransactionId}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-300 font-medium">Product ID</span>
                  <span className="font-mono text-sm bg-gray-800 px-3 py-1 rounded-lg text-white">{meta.productId}</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      case "GOOGLE_PAY":
        if ('googleOrderId' in meta && 'productId' in meta) {
          return (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">G</span>
                  </div>
                </div>
                <h4 className="font-bold text-blue-800 text-lg">Google Pay Details</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-blue-700 font-medium">Order ID</span>
                  <span className="font-mono text-sm bg-blue-200 px-3 py-1 rounded-lg text-blue-800">{meta.googleOrderId}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-blue-700 font-medium">Product ID</span>
                  <span className="font-mono text-sm bg-blue-200 px-3 py-1 rounded-lg text-blue-800">{meta.productId}</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      case "WIX_PAYMENT":
        if ('wixOrderId' in meta && 'wixOrderNumber' in meta && 'productId' in meta && 'productName' in meta && 'buyerEmail' in meta) {
          return (
            <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-200 rounded-lg">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">W</span>
                  </div>
                </div>
                <h4 className="font-bold text-green-800 text-lg">Wix Payment Details</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-green-200">
                  <span className="text-green-700 font-medium">Order ID</span>
                  <span className="font-mono text-sm bg-green-200 px-3 py-1 rounded-lg text-green-800">{meta.wixOrderId}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-200">
                  <span className="text-green-700 font-medium">Order Number</span>
                  <span className="font-mono text-sm bg-green-200 px-3 py-1 rounded-lg text-green-800">{meta.wixOrderNumber}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-200">
                  <span className="text-green-700 font-medium">Product ID</span>
                  <span className="font-mono text-sm bg-green-200 px-3 py-1 rounded-lg text-green-800">{meta.productId}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-green-200">
                  <span className="text-green-700 font-medium">Product Name</span>
                  <span className="font-semibold text-green-800">{meta.productName}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-green-700 font-medium">Buyer Email</span>
                  <span className="font-semibold text-green-800">{meta.buyerEmail}</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
              default:
          return (
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-200 rounded-lg">
                  <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <h4 className="font-bold text-orange-800 text-lg">Unknown Payment Service</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-orange-200">
                  <span className="text-orange-700 font-medium">Service Type</span>
                  <span className="font-mono text-sm bg-orange-200 px-3 py-1 rounded-lg text-orange-800">{transaction.PaymentService}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-orange-700 font-medium">Available Data</span>
                  <span className="text-sm text-orange-800 font-medium">{Object.keys(meta).join(', ')}</span>
                </div>
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error('Error rendering meta info:', error);
      return (
        <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-200 rounded-lg">
              <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">⚠</span>
              </div>
            </div>
            <h4 className="font-bold text-red-800 text-lg">Error Loading Payment Details</h4>
          </div>
          <div className="space-y-3">
            <p className="text-red-700">There was an error loading the payment service details.</p>
            <div className="bg-red-200 px-3 py-2 rounded-lg">
              <span className="text-xs text-red-800 font-mono">Error: {error instanceof Error ? error.message : 'Unknown error'}</span>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="page_container">
      <PageHeader text="Payment Transaction Details" />

      <div
        className={
          "bg-gradient-to-br from-white via-gray-50 to-white rounded-2xl shadow-xl border border-gray-100 p-8 transform transition-all duration-500 ease-out " +
          (isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95")
        }
      >
        {/* Enhanced Header with Background */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl"></div>
          <div className="relative p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-full shadow-lg">
                  <FaCreditCard className="text-2xl text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Transaction #{transaction.id?.slice(-8) || "Unknown"}</h1>
                  <p className="text-gray-600">Payment Transaction Details</p>
                </div>
              </div>
              
              {/* Enhanced Status Badges */}
              <div className="flex flex-wrap gap-3">
                <span
                  className={cx(`inline-flex items-center px-4 py-2 rounded-full border-2 text-sm font-bold shadow-lg ${statusBadgeClass()}`)}
                  style={delayStyle(60)}
                >
                  <div className={`w-2 h-2 rounded-full mr-2 ${transaction.paymentStatus === 'SUCCEEDED' ? 'bg-emerald-500' : transaction.paymentStatus === 'FAILED' ? 'bg-red-500' : transaction.paymentStatus === 'INITIALIZED' ? 'bg-blue-500' : 'bg-gray-500'}`}></div>
                  {transaction.paymentStatus || "Unknown"}
                </span>
                <span
                  className={cx(`inline-flex items-center px-4 py-2 rounded-full border-2 text-sm font-bold shadow-lg ${serviceBadgeClass()}`)}
                  style={delayStyle(120)}
                >
                  {transaction.PaymentService ? transaction.PaymentService.replace("_", " ") : "Unknown"}
                </span>
                {transaction.subscriptionDocId && (
                  <span
                    className={cx("inline-flex items-center px-4 py-2 rounded-full border-2 text-sm font-bold shadow-lg bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border-orange-300")}
                    style={delayStyle(180)}
                  >
                    <FaStar className="w-3 h-3 mr-2" />
                    Subscription Payment
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Left Column - Basic Info */}
          <div className="space-y-6">
            <div className={cx("bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow")} style={delayStyle(80)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaHashtag className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Transaction Information</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Transaction ID</span>
                  <span className="font-mono text-sm bg-gray-100 px-3 py-1 rounded-lg">{transaction.id || "No ID"}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">User ID</span>
                  {transaction.userId ? (
                    <Link
                      to={`/users/${transaction.userId}`}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white text-sm font-semibold shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1"
                    >
                      {transaction.userId}
                    </Link>
                  ) : (
                    <span className="text-gray-400 italic">No User ID</span>
                  )}
                </div>
                {transaction.paymentMethod && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Payment Method</span>
                    <span className="font-semibold text-gray-900">{transaction.paymentMethod}</span>
                  </div>
                )}
              </div>
            </div>

            <div className={cx("bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow")} style={delayStyle(160)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <FaClock className="text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Timestamps</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Created</span>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{transaction.createdAt ? displayDate(transaction.createdAt) : "N/A"}</div>
                    {createdAt && <div className="text-xs text-gray-500">{timeAgo(createdAt)}</div>}
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Updated</span>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{transaction.updatedAt ? displayDate(transaction.updatedAt) : "N/A"}</div>
                    {updatedAt && <div className="text-xs text-gray-500">{timeAgo(updatedAt)}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Financial Info */}
          <div className="space-y-6">
            <div className={cx("bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow")} style={delayStyle(200)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FaCreditCard className="text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Amount Paid</span>
                  <div className="text-right">
                    <div className="font-bold text-xl text-purple-600">{transaction.paymentAmount || "N/A"}</div>
                    <div className="text-sm text-gray-500">{transaction.paymentCurrency || "N/A"}</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Wallet Credit</span>
                  <div className="text-right">
                    <div className="font-bold text-xl text-emerald-600">{transaction.walletAmount || "N/A"}</div>
                    <div className="text-sm text-gray-500">{transaction.walletCurrency || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Service Meta Information */}
            <div className={cx("bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow")} style={delayStyle(240)}>
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <FaInfoCircle className="text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Payment Service Details</h3>
                </div>
              </div>
              <div className="p-6">
                {renderMetaInfo()}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Back Button */}
        {onBack && (
          <div className="flex justify-center">
            <button
              onClick={onBack}
              className={cx("inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2")}
              style={delayStyle(360)}
            >
              <FaArrowLeft className="mr-3 text-lg" /> 
              Back to Transactions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentTransactionDetails;
